/**
 * Cart Page - Dedicated JavaScript
 * Handles all cart operations (add/remove/update) via AJAX
 * NO page reloads, NO custom elements, NO blocking overlays
 */

class CartPage {
  constructor() {
    this.cartContainer = document.getElementById('cart-items-container');
    this.cartForm = document.getElementById('cart');
    this.isUpdating = false;
    this.cartUpdateUnsubscriber = undefined;

    if (!this.cartContainer) {
      console.warn('CartPage: cart container not found');
      return;
    }

    this.init();
  }

  init() {
    // Event delegation for all cart interactions
    this.cartForm.addEventListener('click', (e) => this.handleClick(e));

    // Prevent form submission (everything is AJAX)
    // BUT allow checkout button to submit normally
    this.cartForm.addEventListener('submit', (e) => {
      // Check if the submit was triggered by the checkout button
      const submitter = e.submitter;
      if (submitter && (submitter.name === 'checkout' || submitter.id === 'checkout')) {
        // Allow normal form submission for checkout
        return true;
      }
      // Prevent all other form submissions (quantity updates, etc.)
      e.preventDefault();
      return false;
    });

    // Listen for cart updates from other sources (e.g., "you may also like" products)
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
        // Only handle if we're on the cart page
        if (window.location.pathname === '/cart' || window.location.pathname === '/cart/') {
          await this.refreshCartFromEvent(event);
        }
      });
    }
  }

  handleClick(e) {
    // Remove button (X)
    if (e.target.closest('.remove-btn')) {
      e.preventDefault();
      const btn = e.target.closest('.remove-btn');
      const lineKey = btn.dataset.lineKey;
      this.removeItem(lineKey);
      return;
    }

    // Quantity buttons (+/-)
    const minusBtn = e.target.closest('.quantity-btn[name="minus"]');
    const plusBtn = e.target.closest('.quantity-btn[name="plus"]');

    if (minusBtn || plusBtn) {
      e.preventDefault();
      const input = e.target.closest('.quantity-selector').querySelector('.quantity-input');
      const lineKey = input.dataset.lineKey;
      const currentQty = parseInt(input.value) || 1;

      if (minusBtn) {
        const newQty = Math.max(0, currentQty - 1);
        this.updateQuantity(lineKey, newQty);
      } else if (plusBtn) {
        this.updateQuantity(lineKey, currentQty + 1);
      }
    }
  }

  /**
   * Update item quantity via AJAX
   * If quantity = 0, item is removed
   */
  async updateQuantity(lineKey, quantity) {
    if (this.isUpdating) return;

    this.isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: quantity,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();

      // Update incrementally instead of regenerating everything
      if (quantity === 0) {
        this.removeItemFromDOM(lineKey);
      } else {
        const updatedItem = cart.items.find(item => item.key === lineKey);
        if (updatedItem) {
          this.updateItemInDOM(lineKey, updatedItem);
        }
      }

      // Always update totals and counter
      this.updateCartTotals(cart);
      this.updateHeaderCounter(cart.item_count);

      // If cart is empty, show empty state
      if (cart.item_count === 0) {
        this.showEmptyState();
      }

    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showError(error.message);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Remove item from cart (quantity = 0)
   */
  async removeItem(lineKey) {
    return this.updateQuantity(lineKey, 0);
  }

  /**
   * Refresh cart when item is added from external source (e.g., "you may also like")
   */
  async refreshCartFromEvent(event) {
    if (this.isUpdating) return;

    this.isUpdating = true;

    try {
      // Fetch updated cart
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      const cart = await response.json();

      // Get current item keys in DOM
      const currentItemKeys = Array.from(this.cartContainer.querySelectorAll('[data-line-key]'))
        .map(input => input.dataset.lineKey);

      // Find new items (items in cart but not in DOM)
      const newItems = cart.items.filter(item => !currentItemKeys.includes(item.key));

      // Add new items to DOM
      if (newItems.length > 0) {
        // Fetch the cart items section HTML from Shopify
        const sectionResponse = await fetch(`/cart?section_id=main-cart-items`);
        if (sectionResponse.ok) {
          const sectionHTML = await sectionResponse.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(sectionHTML, 'text/html');
          const newItemsContainer = doc.getElementById('cart-items-container');
          
          if (newItemsContainer) {
            // Extract only the new items HTML
            newItems.forEach(newItem => {
              const newItemElement = newItemsContainer.querySelector(`[data-line-key="${newItem.key}"]`)?.closest('.cart-product-card');
              if (newItemElement) {
                // Clone and append the new item
                const clonedItem = newItemElement.cloneNode(true);
                this.cartContainer.appendChild(clonedItem);
              }
            });
          }
        }
      }

      // Update totals and counter
      this.updateCartTotals(cart);
      this.updateHeaderCounter(cart.item_count);

    } catch (error) {
      console.error('Error refreshing cart:', error);
    } finally {
      this.isUpdating = false;
    }
  }

  /**
   * Remove item from DOM (incremental update)
   */
  removeItemFromDOM(lineKey) {
    const itemElement = document.querySelector(`[data-line-key="${lineKey}"]`)?.closest('.cart-product-card');
    if (itemElement) {
      itemElement.remove();
    }
  }

  /**
   * Update item in DOM (incremental update)
   * Only updates quantity and price, doesn't regenerate HTML
   */
  updateItemInDOM(lineKey, item) {
    const itemElement = document.querySelector(`[data-line-key="${lineKey}"]`)?.closest('.cart-product-card');
    if (!itemElement || !item) return;

    // Debug: dump item object with all properties
    console.log('=== ITEM IN updateItemInDOM ===', item);

    // Update quantity input
    const quantityInput = itemElement.querySelector('.quantity-input');
    if (quantityInput) {
      quantityInput.value = item.quantity;
    }

    // Update price (both mobile and desktop)
    const price = this.formatMoney(item.final_price);
    const priceElements = itemElement.querySelectorAll('.cart-product-price');
    priceElements.forEach(el => {
      el.textContent = price;
    });
  }

  /**
   * Update cart totals (subtotal, discounts, total)
   */
  updateCartTotals(cart) {
    const subtotalElement = document.querySelector('[data-cart-subtotal]');
    const totalElement = document.querySelector('[data-cart-total]');
    const discountElement = document.querySelector('[data-cart-discount]');

    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(cart.original_total_price);
    }

    if (totalElement) {
      totalElement.textContent = this.formatMoney(cart.total_price);
    }

    if (discountElement) {
      if (cart.total_discount > 0) {
        discountElement.textContent = `- ${this.formatMoney(cart.total_discount)}`;
        discountElement.closest('.totale-row').style.display = 'flex';
      } else {
        discountElement.closest('.totale-row').style.display = 'none';
      }
    }
  }

  /**
   * Update header cart counter
   */
  updateHeaderCounter(itemCount) {
    const countBubble = document.querySelector('.cart-count-bubble');
    if (countBubble) {
      const countSpan = countBubble.querySelector('span[aria-hidden="true"]');
      if (countSpan) {
        countSpan.textContent = itemCount;
        countBubble.style.display = itemCount > 0 ? 'flex' : 'none';
      }
    }
  }

  /**
   * Show empty cart state
   */
  showEmptyState() {
    const container = document.querySelector('.carrello-container');
    if (container) {
      // Hide cart content
      document.querySelector('.carrello-content')?.remove();

      // Show empty message
      const emptyHTML = `
        <div class="cart__warnings">
          <h2 class="cart__empty-text">Il carrello è vuoto</h2>
          <a href="/collections/all" class="button">
            Continua lo shopping
          </a>
        </div>
      `;
      container.insertAdjacentHTML('beforeend', emptyHTML);
      container.classList.add('is-empty');
    }
  }

  /**
   * Show error toast
   */
  showError(message) {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #e74c3c;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 9999;
      font-family: var(--font-muli);
      font-size: 14px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.transition = 'opacity 0.3s ease';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  /**
   * Format money (Shopify format)
   */
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `€ ${amount}`;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartPage = new CartPage();
  });
} else {
  window.cartPage = new CartPage();
}
